import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../App';
import { AlertCircle, UserCheck, UserX, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Widget admin — gestión de usuarios inactivos.
 * Muestra los que están por vencer (60-89 días) y los ya desactivados automáticamente.
 * Permite reactivarlos manualmente.
 */
const InactividadAdminWidget = () => {
  const { API, user } = useContext(AuthContext);
  const [porVencer, setPorVencer] = useState([]);
  const [desactivados, setDesactivados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningCron, setRunningCron] = useState(false);

  const isAdmin = user?.rol === 'admin';

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const [pv, ds] = await Promise.all([
        fetch(`${API}/admin/inactividad/por-vencer`, { headers: h }).then(r => r.json()),
        fetch(`${API}/admin/inactividad/desactivados`, { headers: h }).then(r => r.json()),
      ]);
      setPorVencer(pv.users || []);
      setDesactivados(ds.users || []);
    } catch (e) {
      toast.error('No pude cargar la lista');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runCron = async () => {
    setRunningCron(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/admin/inactividad/cron-manual`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await resp.json();
      toast.success(`Cron ejecutado: ${d.warnings_sent} avisos + ${d.deactivated} desactivados`);
      await load();
    } catch (e) {
      toast.error('Error corriendo cron');
    } finally {
      setRunningCron(false);
    }
  };

  const reactivar = async (uid, nombre) => {
    if (!window.confirm(`¿Reactivar la cuenta de ${nombre}?`)) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/admin/inactividad/reactivar/${uid}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Fallo');
      toast.success(`✅ ${nombre} reactivado`);
      await load();
    } catch (e) {
      toast.error('No se pudo reactivar');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="bg-white rounded-2xl shadow-md border p-5" data-testid="inactividad-admin-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-500" />
          <h3 className="font-bold text-gray-800">Gestión de Inactividad</h3>
        </div>
        <button
          onClick={runCron}
          disabled={runningCron}
          className="text-xs px-3 py-1.5 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-medium disabled:opacity-50 flex items-center gap-1"
          data-testid="cron-run-btn"
        >
          {runningCron ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Correr cron ahora
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4 text-sm text-gray-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Por vencer */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">{porVencer.length}</div>
              <h4 className="font-semibold text-sm text-orange-900">Por vencer (60-89 días)</h4>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {porVencer.length === 0 && <p className="text-xs text-gray-500 italic">Ninguno</p>}
              {porVencer.map(u => (
                <div key={u.id} className="bg-white rounded-lg p-2 flex items-center justify-between gap-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{u.nombre} {u.apellido}</p>
                    <p className="text-gray-500 truncate">{u.email}</p>
                    <p className="text-orange-700 mt-0.5">
                      <strong>{u.dias_inactivo}d inactivo</strong> · faltan {u.dias_restantes}d · {u.aviso_enviado ? '✉️ avisado' : '⚠️ sin avisar'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desactivados */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold">{desactivados.length}</div>
              <h4 className="font-semibold text-sm text-red-900">Desactivados (últimos 180d)</h4>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {desactivados.length === 0 && <p className="text-xs text-gray-500 italic">Ninguno</p>}
              {desactivados.map(u => (
                <div key={u.id} className="bg-white rounded-lg p-2 flex items-center justify-between gap-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{u.nombre} {u.apellido}</p>
                    <p className="text-gray-500 truncate">{u.email}</p>
                    <p className="text-red-700 mt-0.5">Motivo: {u.motivo_desactivacion || '—'}</p>
                  </div>
                  <button
                    onClick={() => reactivar(u.id, `${u.nombre} ${u.apellido}`)}
                    className="shrink-0 px-2 py-1 rounded-md bg-green-500 hover:bg-green-600 text-white text-xs font-medium flex items-center gap-1"
                    data-testid={`reactivar-${u.id}`}
                    title="Reactivar cuenta"
                  >
                    <UserCheck className="w-3 h-3" /> Reactivar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
        <UserX className="w-3 h-3" /> El cron corre automáticamente a las 3 AM diarias. Usuarios desactivados pueden reactivarse solos ingresando otra vez.
      </p>
    </div>
  );
};

export default InactividadAdminWidget;
